import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, ArrowLeft, Tag, FolderOpen, Eye } from 'lucide-react';
import { usePromptStore } from '../../store/promptStore';
import PromptPreviewModal from './PromptPreviewModal';

export default function PromptList() {
  const navigate = useNavigate();
  const { 
    prompts, 
    categories, 
    tags, 
    loading, 
    error,
    fetchPrompts,
    fetchCategories,
    fetchTags,
    deletePrompt,
    setSelectedCategory,
    setSelectedTags,
    setSearchQuery,
    selectedCategory,
    selectedTags,
    searchQuery
  } = usePromptStore();
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [previewPromptId, setPreviewPromptId] = useState<string | null>(null);
  
  useEffect(() => {
    fetchPrompts();
    fetchCategories();
    fetchTags();
  }, [fetchPrompts, fetchCategories, fetchTags]);
  
  const handleDelete = async (id: string) => {
    await deletePrompt(id);
    setShowDeleteConfirm(null);
  };
  
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };
  
  const handleTagToggle = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedTags([]);
    setSearchQuery('');
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/prompt-admin')}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Prompts</h1>
            </div>
            <button
              onClick={() => navigate('/prompt-admin/prompts/new')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Prompt
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#F98B3D] focus:border-[#F98B3D] sm:text-sm"
                  placeholder="Search prompts..."
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#F98B3D] focus:border-[#F98B3D] sm:text-sm rounded-md"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          </div>
          
          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => handleTagToggle(tag.id)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                      selectedTags.includes(tag.id)
                        ? 'bg-[#F98B3D] text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Prompts List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        ) : prompts.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <p className="text-gray-500 mb-4">No prompts found with the current filters.</p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {prompts.map((prompt) => (
                <li key={prompt.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <p 
                          className="text-sm font-medium text-[#F98B3D] truncate cursor-pointer hover:underline"
                          onClick={() => navigate(`/prompt-admin/prompts/${prompt.id}`)}
                        >
                          {prompt.title}
                        </p>
                        {prompt.is_featured && (
                          <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        )}
                      </div>
                      <div className="ml-2 flex-shrink-0 flex">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewPromptId(prompt.id);
                          }}
                          className="mr-2 text-gray-400 hover:text-gray-500"
                          title="Preview"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/prompt-admin/prompts/${prompt.id}`)}
                          className="mr-2 text-gray-400 hover:text-gray-500"
                          title="Edit"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(prompt.id)}
                          className="text-red-400 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          <FolderOpen className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {prompt.prompt_categories?.name || 'Uncategorized'}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          <Tag className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {prompt.prompt_tags_map?.length 
                            ? prompt.prompt_tags_map.map((tm: any) => tm.prompt_tags.name).join(', ')
                            : 'No tags'}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <p>
                          Updated {new Date(prompt.updated_at).toLocaleDateString()}
                          {prompt.published_at ? (
                            <span className="ml-2">
                              • Published {prompt.published_at.split('T')[0]}
                            </span>
                          ) : prompt.is_published ? (
                            <span className="ml-2">
                              • Published (no date)
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Delete Confirmation Modal */}
                  {showDeleteConfirm === prompt.id && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Delete Prompt
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Are you sure you want to delete "{prompt.title}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleDelete(prompt.id)}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
      
      {/* Preview Modal */}
      {previewPromptId && (
        <PromptPreviewModal
          promptId={previewPromptId}
          onClose={() => setPreviewPromptId(null)}
        />
      )}
    </div>
  );
}